import { T, useT } from '@transifex/react'
import React from 'react'
import {
  DefinedFulfilmentType,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import { FulfilmentSelect } from '../../../FulfilmentSelect/FulfilmentSelect'
import { LineItemFulfilmentNote } from '../../LineItemFulfilmentNote'

export const TContext = 'Line item fulfilment popover'
export const TestId = 'lif-details-view'

export type LineItemFulfilmentDetailsViewProps = {
  productName: string
  fulfilmentType?: DefinedFulfilmentType
  fulfilmentNote?: string
  onSelect: (opt: DefinedFulfilmentType) => void
  onNoteChange: (note: string) => void
  showSpecialOrderMessage?: boolean
}

export const LineItemFulfilmentDetailsView: React.FC<LineItemFulfilmentDetailsViewProps> = ({
  productName,
  fulfilmentType = FulfilmentType.DELIVERY,
  fulfilmentNote = '',
  onSelect,
  onNoteChange,
  showSpecialOrderMessage = true,
}: LineItemFulfilmentDetailsViewProps) => {
  const t = useT()
  const ffSpecialOrder = getFeatureFromCache('inventory_special_orders')
  const onTypeChanged = (type: FulfilmentType) => {
    if (type === FulfilmentType.UNSPECIFIED) {
      return
    }

    onSelect(type as DefinedFulfilmentType)
  }

  return (
    <div className="uc-base font-general" data-testid={TestId}>
      <div className="flex flex-col gap-6">
        <h3 className="typography-body-sm-emphasized text-fg-neutral-default">
          {t('Choose how “{productName}” will be fulfilled', {
            _context: TContext,
            productName,
          })}
        </h3>
        {ffSpecialOrder ? (
          <FulfilmentSelect
            showUnspecified={false}
            defaultType={fulfilmentType}
            onTypeChange={onTypeChanged}
            stretched={true}
            message={t(
              'You can select one method per sale. To set another method, create another sale.',
              {
                _context: 'Note explaining how fulfillment methods work',
              }
            )}
            outOfStockMessage={
              showSpecialOrderMessage
                ? t(
                    'This product is out of stock. If incoming stock is insufficient, it will be available for special order and ready to be fulfilled upon restock.',
                    {
                      _context: 'Special order product info message',
                    }
                  )
                : undefined
            }
          />
        ) : (
          <>
            <p className="typography-body-sm text-fg-neutral-default">
              <T
                _str="Only one fulfillment method can be selected for the items that require fulfillment. To record another method, create another sale."
                _context={TContext}
              />
            </p>
            <p className="typography-body-sm text-fg-neutral-default">
              {t(
                'Record the fulfillment details for “{productName}” in the fulfillment note.',
                {
                  _context: TContext,
                  productName,
                }
              )}
            </p>
            <FulfilmentSelect
              showUnspecified={false}
              defaultType={fulfilmentType}
              onTypeChange={onTypeChanged}
              stretched={true}
            />
          </>
        )}

        <LineItemFulfilmentNote note={fulfilmentNote} onChange={onNoteChange} />
      </div>
    </div>
  )
}
